// ═══════════════════════════════════════════════════════════════════════
// treeLayout.js – wo steht welche Karte im Stammbaum, und wo laufen die Linien?
// ═══════════════════════════════════════════════════════════════════════
// Drei Teile:
//   1. assignGenerations – in welchem „Stockwerk“ (Generation) steht jede Person?
//   2. computeLayout     – genaue x/y-Position jeder Karte auf der Zeichenfläche
//   3. computeLines      – die Verbindungslinien (Paare, Eltern → Kinder)
// Gezeichnet wird das Ergebnis in TreeCanvas.jsx.
// treeLayout.js
//
// 3-PASS GENERATIONSALGORITHMUS:
//   Pass 1: BFS nur über parent-child Kanten (Partner ignoriert)
//   Pass 2: Partner auf gleiche Gen bringen (Gewinner = mehr parent-child Verbindungen)
//   Pass 3: parent-child Konsistenz erzwingen: kind.gen MUSS = parent.gen + 1
//           Partner werden mitgezogen

// Grösse der Karten im Stammbaum und Abstände (in Bildschirm-Pixeln, vor dem Zoomen):
// CARD_W/CARD_H = Breite/Höhe einer Karte, H_GAP = Abstand nebeneinander,
// V_GAP = Abstand zwischen den Generationen, COUPLE_GAP = Abstand innerhalb eines Paares.
export const CARD_W     = 140;
export const CARD_H     = 160;
export const H_GAP      = 28;
export const V_GAP      = 70;
export const COUPLE_GAP = 28;

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

// ─── 1. GENERATIONEN ─────────────────────────────────────────────────────────

// Ergebnis: Map Person-id → Generationsnummer (kleinere Zahl = weiter oben).
export function assignGenerations(persons, relationships) {
  if (persons.length === 0) return new Map();

  const allIds = persons.map(p => p.id);

  // Indexes
  // Nachschlagetabellen aufbauen. delta +1 heisst „eine Generation tiefer“ (Kind),
  // −1 „eine höher“ (Elternteil).
  const pcEdges  = new Map();   // id -> [{neighbor, delta}] — nur parent-child
  const hasParent = new Set();
  const pcCount  = new Map();   // id -> Anzahl parent-child Verbindungen
  const partnerRels = [];       // partner/ex-partner Beziehungen
  const childrenOf  = new Map(); // parentId -> [childId]  (für Pass 3)
  const partnerOf   = new Map(); // id -> [partnerId]       (für Pass 3)

  relationships.forEach(r => {
    if (r.type === 'parent-child') {
      if (!pcEdges.has(r.from)) pcEdges.set(r.from, []);
      if (!pcEdges.has(r.to))   pcEdges.set(r.to,   []);
      pcEdges.get(r.from).push({ neighbor: r.to,   delta: +1 });
      pcEdges.get(r.to  ).push({ neighbor: r.from, delta: -1 });
      hasParent.add(r.to);
      pcCount.set(r.from, (pcCount.get(r.from) || 0) + 1);
      pcCount.set(r.to,   (pcCount.get(r.to)   || 0) + 1);
      if (!childrenOf.has(r.from)) childrenOf.set(r.from, []);
      childrenOf.get(r.from).push(r.to);
    } else if (r.type === 'partner' || r.type === 'ex-partner') {
      partnerRels.push(r);
      if (!partnerOf.has(r.from)) partnerOf.set(r.from, []);
      if (!partnerOf.has(r.to))   partnerOf.set(r.to,   []);
      partnerOf.get(r.from).push(r.to);
      partnerOf.get(r.to).push(r.from);
    }
  });

  // ── Pass 1: BFS nur über parent-child ──────────────────────────────
  // Start bei den „echten“ obersten Personen (Generation 0), dann über Eltern-Kind-
  // Verbindungen weiter: Kind = +1, Elternteil = −1.
  // Eingeheiratete Personen (ohne Eltern, aber mit einem Partner aus der Familie –
  // auch über mehrere Partner hinweg, z.B. die Expartnerin eines eingeheirateten
  // Partners) sind KEINE Startpunkte: Sie bekommen die Generation ihres Partners.
  // Sonst würden sie fälschlich ganz oben beginnen und eine Generation zu hoch landen.
  const partnerComp = new Map(); // id → Kennung der Partner-Gruppe
  allIds.forEach(id => {
    if (partnerComp.has(id)) return;
    const stack = [id];
    partnerComp.set(id, id);
    while (stack.length) {
      const cur = stack.pop();
      (partnerOf.get(cur) || []).forEach(p => { if (!partnerComp.has(p)) { partnerComp.set(p, id); stack.push(p); } });
    }
  });
  const compHasParent = new Set(allIds.filter(id => hasParent.has(id)).map(id => partnerComp.get(id)));
  const roots   = allIds.filter(id => !hasParent.has(id) && !compHasParent.has(partnerComp.get(id)));
  const seeds   = roots.length > 0 ? roots : [allIds[0]];
  const genMap  = new Map();
  const visited = new Set();
  const queue   = [];

  seeds.forEach(id => {
    if (!visited.has(id)) { visited.add(id); genMap.set(id, 0); queue.push(id); }
  });

  // Breitensuche; danach Partner ohne Generation von ihrem Partner übernehmen und
  // von dort aus weitersuchen – so lange, bis sich nichts mehr ändert.
  const runBfs = () => {
    while (queue.length) {
      const id  = queue.shift();
      const gen = genMap.get(id);
      for (const { neighbor, delta } of (pcEdges.get(id) || [])) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          genMap.set(neighbor, gen + delta);
          queue.push(neighbor);
        }
      }
    }
  };
  runBfs();
  for (let more = true; more;) {
    more = false;
    for (const id of allIds) {
      if (visited.has(id)) continue;
      const p = (partnerOf.get(id) || []).find(x => visited.has(x));
      if (p === undefined) continue;
      visited.add(id); genMap.set(id, genMap.get(p)); queue.push(id);
      more = true;
    }
    runBfs();
    if (!more) {
      // Noch unverbundene Teile (z.B. eine Person ohne jede Verbindung): neu starten
      const rest = allIds.find(id => !visited.has(id));
      if (rest !== undefined) { visited.add(rest); genMap.set(rest, 0); queue.push(rest); runBfs(); more = true; }
    }
  }
  // Personen ohne jede Verbindung landen in Generation 0.
  allIds.forEach(id => { if (!genMap.has(id)) genMap.set(id, 0); });

  // ── Pass 2: Partner angleichen ──────────────────────────────────────
  // Wer mehr parent-child Verbindungen hat, bestimmt die Generation
  // safety verhindert Endlosschleifen bei widersprüchlichen Daten.
  let changed = true, safety = 0;
  while (changed && safety++ < 100) {
    changed = false;
    partnerRels.forEach(r => {
      const ga = genMap.get(r.from);
      const gb = genMap.get(r.to);
      if (ga === undefined || gb === undefined || ga === gb) return;
      const ca = pcCount.get(r.from) || 0;
      const cb = pcCount.get(r.to)   || 0;
      const anchor = ca >= cb ? ga : gb;
      genMap.set(r.from, anchor);
      genMap.set(r.to,   anchor);
      changed = true;
    });
  }

  // ── Pass 3: parent-child Konsistenz erzwingen ──────────────────────
  // Nach Pass 2 könnten Eltern verschoben worden sein →
  // Kind.gen MUSS = Eltern.gen + 1 gelten. Falls nicht: Kind + Nachkommen verschieben.
  let changed3 = true; safety = 0;
  while (changed3 && safety++ < 200) {
    changed3 = false;
    relationships.forEach(r => {
      if (r.type !== 'parent-child') return;
      const pg = genMap.get(r.from);
      const cg = genMap.get(r.to);
      if (pg === undefined || cg === undefined) return;
      const expected = pg + 1;
      if (cg === expected) return;

      const diff = expected - cg;

      // BFS: verschiebe das Kind und alle seine Nachkommen + deren Partner
      const toShift = [r.to];
      const shifted = new Set();
      while (toShift.length) {
        const node = toShift.shift();
        if (shifted.has(node)) continue;
        shifted.add(node);
        genMap.set(node, (genMap.get(node) || 0) + diff);
        // Kinder nachziehen
        (childrenOf.get(node) || []).forEach(child => {
          if (!shifted.has(child)) toShift.push(child);
        });
        // Partner auf gleicher Ebene mitziehen
        (partnerOf.get(node) || []).forEach(partner => {
          if (!shifted.has(partner)) {
            genMap.set(partner, (genMap.get(partner) || 0) + diff);
            shifted.add(partner);
            // Kinder des Partners auch nachziehen
            (childrenOf.get(partner) || []).forEach(child => {
              if (!shifted.has(child)) toShift.push(child);
            });
          }
        });
      }
      changed3 = true;
    });
  }

  return genMap;
}

// ─── 2. LAYOUT ───────────────────────────────────────────────────────────────
//
// Slot-Typen:
//   single   : { type:'single', id }
//   extended : { type:'extended', ids:[...exPartners, mainId, currentPartner?] }
//              Reihenfolge: Expartner(n) links → Hauptperson → aktueller Partner rechts
//              Die "Hauptperson" ist die Person mit Eltern in dieser Gen, oder die erste.
//              slotWidth = Anzahl * CARD_W + (Anzahl-1) * COUPLE_GAP
//
// Positionierung: setSlotPos setzt alle ids[i] auf x + i*(CARD_W+COUPLE_GAP)

// Ergebnis: Map Person-id → { x, y } (linke obere Ecke der Karte).
// Personen einer Generation werden zu „Slots“ zusammengefasst: Einzelperson oder
// Paar-Gruppe (Ex-Partner links, Person, aktueller Partner rechts). Ein Slot wird
// immer als Ganzes platziert.
export function computeLayout(persons, relationships) {
  if (persons.length === 0) return new Map();

  const genMap = assignGenerations(persons, relationships);

  const childrenOf  = new Map();
  const parentsOf   = new Map();
  const currentPartnerOf = new Map(); // id -> [id]  (partner)
  const exPartnerOf      = new Map(); // id -> [id]  (ex-partner)

  relationships.forEach(r => {
    if (r.type === 'parent-child') {
      if (!childrenOf.has(r.from)) childrenOf.set(r.from, []);
      childrenOf.get(r.from).push(r.to);
      if (!parentsOf.has(r.to)) parentsOf.set(r.to, []);
      parentsOf.get(r.to).push(r.from);
    } else if (r.type === 'partner') {
      if (!currentPartnerOf.has(r.from)) currentPartnerOf.set(r.from, []);
      if (!currentPartnerOf.has(r.to))   currentPartnerOf.set(r.to,   []);
      currentPartnerOf.get(r.from).push(r.to);
      currentPartnerOf.get(r.to).push(r.from);
    } else if (r.type === 'ex-partner') {
      if (!exPartnerOf.has(r.from)) exPartnerOf.set(r.from, []);
      if (!exPartnerOf.has(r.to))   exPartnerOf.set(r.to,   []);
      exPartnerOf.get(r.from).push(r.to);
      exPartnerOf.get(r.to).push(r.from);
    }
  });

  const personById = new Map(persons.map(p => [p.id, p]));

  // Personen nach Generation gruppieren.
  const byGen = new Map();
  persons.forEach(p => {
    const g = genMap.get(p.id);
    if (!byGen.has(g)) byGen.set(g, []);
    byGen.get(g).push(p.id);
  });

  // Sortierung erfolgt NACH der Slot-Erstellung (siehe unten), damit
  // nur die Hauptperson eines Slots das Alter bestimmt, nicht die Partner.

  // Alle Partner-IDs einer Person (current + ex) in derselben Gen
  function allPartnersInGen(id, idSet) {
    return [
      ...(currentPartnerOf.get(id) || []),
      ...(exPartnerOf.get(id) || [])
    ].filter(pid => idSet.has(pid));
  }

  // Baut die Slots einer Generation.
  function getSlotsForGen(ids) {
    const idSet  = new Set(ids);
    const placed = new Set();
    const slots  = [];

    ids.forEach(firstId => {
      if (placed.has(firstId)) return;

      const allP0 = allPartnersInGen(firstId, idSet).filter(p => !placed.has(p));
      if (allP0.length === 0) {
        placed.add(firstId);
        slots.push({ type: 'single', id: firstId });
        return;
      }
      // Mittelpunkt der Gruppe: zuerst eine Person aus der Familie (mit Eltern im
      // Stammbaum), dann die mit den meisten Partnern (z.B. Erika mit zwei Expartnern
      // und einem Partner). So steht z.B. Jaqueline in der Mitte und nicht ihr Partner
      // Patric, auch wenn Patric zusätzlich eine Expartnerin hat.
      const score = pid => ((parentsOf.get(pid) || []).length ? 100 : 0) + allPartnersInGen(pid, idSet).length;
      const id = [firstId, ...allP0].reduce((best, pid) => (score(pid) > score(best) ? pid : best), firstId);

      // Baue extended slot:
      // Expartner links, id in der Mitte, aktueller Partner rechts
      // Reihenfolge der Expartner: ohne gemeinsame Kinder ganz links, sonst nach dem
      // ältesten gemeinsamen Kind (frühere Beziehung weiter links, also weiter weg).
      const firstKid = pid => {
        const kids = (childrenOf.get(id) || []).filter(k => (parentsOf.get(k) || []).includes(pid));
        const dates = kids.map(k => personById.get(k)?.birthDate || '9999');
        return kids.length ? dates.sort()[0] : '';
      };
      const exPs      = (exPartnerOf.get(id) || []).filter(p => idSet.has(p) && !placed.has(p))
        .sort((a, b) => (firstKid(a) < firstKid(b) ? -1 : firstKid(a) > firstKid(b) ? 1 : 0));
      const curPs     = (currentPartnerOf.get(id) || []).filter(p => idSet.has(p) && !placed.has(p));
      const slotIds   = [...exPs, id, ...curPs];
      slotIds.forEach(sid => placed.add(sid));
      // „Brücke“: Ein Paar, bei dem beide Partner Eltern im Stammbaum haben (zwei
      // Familien verbinden sich). Für die Sortierung zählt dann die Familie des Partners.
      // Brücken-Slot erkennen: Partner kommt aus einer anderen gen0-Familie
      const mainParentKey = (parentsOf.get(id) || []).slice().sort().join('|');
      let bridgePartnerId = null;
      if (mainParentKey) {
        for (const pid of [...exPs, ...curPs]) {
          const pp = (parentsOf.get(pid) || []).slice().sort().join('|');
          if (pp && pp !== mainParentKey) { bridgePartnerId = pid; break; }
        }
      }
      // Der Partner aus der anderen Familie steht ganz aussen (Expartner: ganz links,
      // aktueller Partner: ganz rechts) – also möglichst nahe bei seiner Familie.
      if (bridgePartnerId && exPs.includes(bridgePartnerId)) {
        exPs.splice(exPs.indexOf(bridgePartnerId), 1);
        exPs.unshift(bridgePartnerId);
      } else if (bridgePartnerId && curPs.includes(bridgePartnerId)) {
        curPs.splice(curPs.indexOf(bridgePartnerId), 1);
        curPs.push(bridgePartnerId);
      }
      // Weitere Partner der Partner (z.B. die Expartnerin von Patric) kommen direkt
      // neben „ihren“ Partner, auf die Aussenseite: Jaqueline, Patric, Expartnerin.
      const othersOf = pid => allPartnersInGen(pid, idSet).filter(q => q !== id && !placed.has(q) && !exPs.includes(q) && !curPs.includes(q));
      const left = [], right = [];
      exPs.forEach(pid => { const o = othersOf(pid); o.forEach(q => placed.add(q)); left.push(...o, pid); });
      curPs.forEach(pid => { const o = othersOf(pid); o.forEach(q => placed.add(q)); right.push(pid, ...o); });
      const orderedIds = [...left, id, ...right];
      slots.push({ type: 'extended', ids: orderedIds, mainId: id, curPartners: curPs, exPartners: exPs, bridgePartnerId });
    });

    return slots;
  }

  // Setze alle Karten eines Slots auf Position x, y (x = linker Rand des Slots).
  // Die Abstände der Karten innerhalb des Slots berechnet layoutOf (Expartner
  // können weiter nach aussen rücken, damit sie über ihren Kindern stehen).
  function placeSlot(slot, x, y) {
    layoutOf(slot).off.forEach((o, sid) => positions.set(sid, { x: x + o, y }));
  }

  // Alle IDs eines Slots
  function slotIds(slot) {
    return slot.type === 'single' ? [slot.id] : slot.ids;
  }

  // genY = senkrechte Position einer Generation.
  const sortedGens = [...byGen.keys()].sort((a, b) => a - b);
  const minGen     = sortedGens[0];
  const genY       = g => (g - minGen) * (CARD_H + V_GAP);
  const slotsByGen = new Map();
  sortedGens.forEach(g => {
    const slots = getSlotsForGen(byGen.get(g));

    // Geschwister-Sortierung: nur innerhalb derselben Elterngruppe nach Alter
    // Die Reihenfolge der Gruppen untereinander bleibt unverändert.
    // Schlüssel der Eltern eines Slots (z.B. „id1|id2“). Slots mit gleichem Schlüssel sind Geschwister.
    const parentKey = slot => {
      // Brücken-Slot: parentKey des Bridge-Partners (andere Familie) verwenden
      if (slot.bridgePartnerId) {
        const pp = (parentsOf.get(slot.bridgePartnerId) || []).slice().sort().join('|');
        if (pp) return pp;
      }
      const mainId = slot.type === 'single' ? slot.id : slot.mainId;
      let parents = (parentsOf.get(mainId) || []).slice().sort().join('|');
      // Falls mainId keine Eltern hat, prüfe ob ein Partner Eltern hat
      if (!parents && slot.type !== 'single') {
        for (const pid of slot.ids) {
          if (pid === mainId) continue;
          const pp = (parentsOf.get(pid) || []).slice().sort().join('|');
          if (pp) { parents = pp; break; }
        }
      }
      return parents || `__no_parent__${mainId}`;
    };

    // Gruppiere Slots (Reihenfolge der Gruppen beibehalten)
    const groups = new Map();
    const groupOrder = [];
    slots.forEach(slot => {
      const key = parentKey(slot);
      if (!groups.has(key)) { groups.set(key, []); groupOrder.push(key); }
      groups.get(key).push(slot);
    });

    // Innerhalb jeder Geschwistergruppe nach Geburtsdatum sortieren.
    // Brücken-Slots: Geburtsdatum des Bridge-Partners verwenden (nicht mainId),
    // damit z.B. [Erika|Andreas] nach Andreas' Alter in der Annerose+Wolfgang-Gruppe einsortiert wird.
    groups.forEach(group => {
      group.sort((sA, sB) => {
        const idA = sA.bridgePartnerId || (sA.type === 'single' ? sA.id : sA.mainId);
        const idB = sB.bridgePartnerId || (sB.type === 'single' ? sB.id : sB.mainId);
        const da  = personById.get(idA)?.birthDate || '';
        const db  = personById.get(idB)?.birthDate || '';
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da < db ? -1 : da > db ? 1 : 0;
      });
    });

    // Gruppen in ursprünglicher Reihenfolge zusammensetzen
    const sortedSlots = groupOrder.flatMap(key => groups.get(key));
    slotsByGen.set(g, sortedSlots);
  });

  const positions = new Map();

  // ── Positionierung über Teilbaum-Breiten ─────────────────────────────
  // Jeder Slot bekommt ein horizontales Intervall, das breit genug für ihn
  // selbst UND den ganzen Block seiner Nachkommen ist. Kinder stehen so immer
  // direkt unter ihren Eltern, Geschwistergruppen überlappen nie, und Eltern
  // rücken automatisch auseinander, wenn ihre Kinder mehr Platz brauchen.

  const slotOf    = new Map(); // personId -> slot
  const genOfSlot = new Map(); // slot -> gen
  slotsByGen.forEach((slots, g) => slots.forEach(s => {
    genOfSlot.set(s, g);
    slotIds(s).forEach(id => slotOf.set(id, s));
  }));

  // Person, die bestimmt, zu welchen Eltern ein Slot gehört
  const anchorIdOf = s => s.type === 'single' ? s.id : (s.bridgePartnerId || s.mainId);

  // Eltern-Slot jedes Slots bestimmen (muss genau eine Generation höher liegen).
  // Sonderfall „Brücke“ (z.B. Sabine aus Familie A mit Expartner aus Familie B):
  // Sind beide Elternpaare oberste Paare, wird der Slot als LETZTES Kind der linken
  // Familie eingereiht (bridgeLast). So steht er genau zwischen den zwei Familien
  // und beide Elternpaare erreichen ihn mit kurzen Linien.
  const kidsOfSlot   = new Map(); // parentSlot -> [kidSlot]
  const parentSlotOf = new Map(); // kidSlot -> parentSlot
  const kidParentIds = new Map(); // kidSlot -> [parentIds]
  const parentSlotFor = (pIds, g) => {
    const cand = pIds.map(pid => slotOf.get(pid)).filter(ps => ps && genOfSlot.get(ps) === g - 1);
    if (!cand.length) return null;
    // Slot, der die meisten Elternteile enthält
    return cand.slice().sort((a, b) =>
      cand.filter(x => x === b).length - cand.filter(x => x === a).length)[0];
  };
  sortedGens.forEach(g => {
    slotsByGen.get(g).forEach(s => {
      let pIds = parentsOf.get(anchorIdOf(s)) || [];
      if (!pIds.length && s.type !== 'single') {
        for (const id of s.ids) {
          const pp = parentsOf.get(id) || [];
          if (pp.length) { pIds = pp; break; }
        }
      }
      let ps = parentSlotFor(pIds, g);
      if (s.bridgePartnerId) {
        const mainP = parentsOf.get(s.mainId) || [];
        const psMain = parentSlotFor(mainP, g);
        if (ps && psMain && ps !== psMain && !parentSlotOf.has(ps) && !parentSlotOf.has(psMain)) {
          const order = slotsByGen.get(g - 1);
          if (order.indexOf(psMain) < order.indexOf(ps)) { ps = psMain; pIds = mainP; }
          s.bridgeLast = true;
          // Der Partner aus der anderen Familie soll auf der Seite seiner Familie stehen.
          // Normal stehen Expartner links und aktuelle Partner rechts. Passt das nicht
          // (z.B. Sabines Expartner Robert, dessen Familie rechts steht), wird die
          // ganze Gruppe gespiegelt: Beat, Sabine, Jean-Pierre, Robert.
          const bridgeFamilyRight = ps === psMain;
          const bridgeIsLeft = s.exPartners.includes(s.bridgePartnerId);
          s.mirror = bridgeFamilyRight === bridgeIsLeft;
        }
      }
      if (!ps) return;
      parentSlotOf.set(s, ps);
      kidParentIds.set(s, pIds);
      if (!kidsOfSlot.has(ps)) kidsOfSlot.set(ps, []);
      kidsOfSlot.get(ps).push(s);
    });
  });

  // Grund-Abstände der Karten eines Slots, relativ zur Hauptperson (= 0):
  // Expartner links davon, aktuelle Partner rechts, je eine Karte + Lücke.
  function baseOff(s) {
    const ids = slotIds(s);
    const mainIdx = s.type === 'single' ? 0 : ids.indexOf(s.mainId);
    return new Map(ids.map((id, i) => [id, (i - mainIdx) * (CARD_W + COUPLE_GAP)]));
  }
  // Wo sollen die Kinder eines Elternpaares stehen? c = gewünschte Mitte (in den
  // Koordinaten von off), exId = Expartner, unter dem sie stehen (sonst null).
  //   • Kinder mit einem Expartner: mittig unter dem Expartner
  //   • Kinder eines Paares nebeneinander: mittig unter der Lücke zwischen den beiden
  //   • nur ein Elternteil im Slot: mittig unter dieser Karte
  function anchorOf(s, pIds, off) {
    const ids = slotIds(s);
    const inSlot = pIds.filter(pid => ids.includes(pid));
    if (inSlot.length === 0) {
      const vals = [...off.values()];
      return { c: (Math.min(...vals) + Math.max(...vals) + CARD_W) / 2, exId: null };
    }
    if (inSlot.length === 1) return { c: off.get(inSlot[0]) + CARD_W / 2, exId: inSlot[0] };
    const [a, b] = inSlot;
    if ((exPartnerOf.get(a) || []).includes(b)) {
      // Expartner = wer weiter von der Mitte (Hauptperson) entfernt steht
      const ia = ids.indexOf(a), ib = ids.indexOf(b), im = ids.indexOf(s.mainId);
      const exId = Math.abs(ia - im) >= Math.abs(ib - im) ? a : b;
      return { c: off.get(exId) + CARD_W / 2, exId };
    }
    const xa = off.get(a), xb = off.get(b);
    return { c: (Math.min(xa, xb) + CARD_W + Math.max(xa, xb)) / 2, exId: null };
  }

  // Kinder eines Slots nach Elternpaar sortieren (Paare von links nach rechts),
  // innerhalb eines Paares bleibt die Altersreihenfolge erhalten; ein Brücken-Slot
  // kommt ans Ende.
  kidsOfSlot.forEach((kids, ps) => {
    const off = baseOff(ps);
    const withIdx = kids.map((k, i) => ({ k, i, o: anchorOf(ps, kidParentIds.get(k) || [], off).c, b: k.bridgeLast ? 1 : 0 }));
    withIdx.sort((A, B) => (A.o - B.o) || (A.b - B.b) || (A.i - B.i));
    kidsOfSlot.set(ps, withIdx.map(x => x.k));
  });

  // ── Kinder unter ihre Eltern legen ─────────────────────────────────────
  // Die Kinder eines Slots bilden Gruppen – eine pro Elternpaar.
  //   1. Gruppen unter der Hauptperson und ihrem aktuellen Partner: jede möchte genau
  //      unter ihrem Elternpaar stehen. Stossen sie zusammen, werden sie so wenig wie
  //      möglich verschoben („isotone Regression“: Gruppen, die sich stossen, werden
  //      zusammengelegt und gemeinsam um die mittlere Wunsch-Position zentriert).
  //   2. Expartner (von innen nach aussen): Ihre Kinder stehen immer GENAU mittig
  //      unter ihnen. Fehlt der Platz, rückt der Expartner weiter nach links –
  //      die Lücke zwischen den Karten wird grösser. So kreuzen sich keine Linien.
  // Ergebnis (relativ zum linken Rand des Slots): off = Position jeder Karte,
  // L/R = linker/rechter Rand des ganzen Teilbaums, groups = [{ x, w, kids }].
  const layoutMemo = new Map();
  function layoutOf(s) {
    if (layoutMemo.has(s)) return layoutMemo.get(s);
    const ids = slotIds(s);
    const mainIdx = s.type === 'single' ? 0 : ids.indexOf(s.mainId);
    // Expartner links der Hauptperson – und rechts davon die Expartner der Partner
    // (z.B. Test Test rechts von Anias), aber nie der direkte aktuelle Partner.
    const exSet = new Set(ids.slice(0, mainIdx));
    const curSet = new Set(s.curPartners || []);
    const rightExSet = new Set(ids.slice(mainIdx + 1).filter(id => !curSet.has(id)));
    const off = baseOff(s);
    const right = [];
    const exGroups = new Map();
    (kidsOfSlot.get(s) || []).forEach(k => {
      const a = anchorOf(s, kidParentIds.get(k) || [], off);
      if (a.exId && (exSet.has(a.exId) || rightExSet.has(a.exId))) {
        if (!exGroups.has(a.exId)) exGroups.set(a.exId, { kids: [] });
        exGroups.get(a.exId).kids.push(k);
        return;
      }
      const last = right[right.length - 1];
      if (last && Math.abs(last.c - a.c) < 0.5) last.kids.push(k);
      else right.push({ c: a.c, kids: [k] });
    });
    const widthOf = g => g.kids.reduce((sum, k) => sum + subW(k), 0) + H_GAP * (g.kids.length - 1);
    right.forEach(g => { g.w = widthOf(g); });
    exGroups.forEach(g => { g.w = widthOf(g); });

    // 1. Isotone Regression: y_i = x_i − (Breite aller Gruppen links davon) muss steigen.
    let before = 0;
    const blocks = [];
    right.forEach(g => {
      g.before = before;
      let blk = { sum: g.c - g.w / 2 - before, n: 1, items: [g] };
      while (blocks.length && blocks[blocks.length - 1].sum / blocks[blocks.length - 1].n > blk.sum / blk.n) {
        const prev = blocks.pop();
        blk = { sum: prev.sum + blk.sum, n: prev.n + blk.n, items: [...prev.items, ...blk.items] };
      }
      blocks.push(blk);
      before += g.w + H_GAP;
    });
    blocks.forEach(b => b.items.forEach(g => { g.x = b.sum / b.n + g.before; }));

    // 2. Expartner von innen nach aussen platzieren
    let leftBound = right.length ? right[0].x - H_GAP : Infinity;
    const exList = [];
    for (let i = mainIdx - 1; i >= 0; i--) {
      const id = ids[i];
      let o = off.get(ids[i + 1]) - (CARD_W + COUPLE_GAP);
      const g = exGroups.get(id);
      if (g) {
        o = Math.min(o, leftBound - g.w / 2 - CARD_W / 2);
        g.x = o + CARD_W / 2 - g.w / 2;
        leftBound = g.x - H_GAP;
        exList.unshift(g);
      }
      off.set(id, o);
    }
    // 3. Expartner rechts (von innen nach aussen): mittig über ihren Kindern; fehlt der
    //    Platz, rücken sie nach rechts.
    let rightBound = right.length ? right[right.length - 1].x + right[right.length - 1].w + H_GAP : -Infinity;
    const exRight = [];
    for (let i = mainIdx + 1; i < ids.length; i++) {
      const id = ids[i];
      let o = off.get(ids[i - 1]) + (CARD_W + COUPLE_GAP);
      const g = rightExSet.has(id) ? exGroups.get(id) : null;
      if (g) {
        o = Math.max(o, rightBound + g.w / 2 - CARD_W / 2);
        g.x = o + CARD_W / 2 - g.w / 2;
        rightBound = g.x + g.w + H_GAP;
        exRight.push(g);
      }
      off.set(id, o);
    }
    const groups = [...exList, ...right, ...exRight];

    // Alles so verschieben, dass die linke Karte bei 0 steht.
    const minOff = Math.min(...off.values());
    off.forEach((o, id) => off.set(id, o - minOff));
    groups.forEach(g => { g.x -= minOff; });
    const width = Math.max(...off.values()) + CARD_W;
    let L = 0, R = width;
    groups.forEach(g => { L = Math.min(L, g.x); R = Math.max(R, g.x + g.w); });
    if (s.mirror) {
      // Gespiegelt: rechts wird links. Die Kinder innerhalb einer Gruppe behalten
      // ihre Reihenfolge (älteste links).
      off.forEach((o, id) => off.set(id, width - CARD_W - o));
      groups.forEach(g => { g.x = width - g.x - g.w; });
      [L, R] = [width - R, width - L];
    }
    const res = { L, R, groups, off };
    layoutMemo.set(s, res);
    return res;
  }
  function subW(s) { const l = layoutOf(s); return l.R - l.L; }

  // Platziert einen Slot und darunter rekursiv alle Nachkommen. left = linker Rand
  // des Bereichs, den dieser Teilbaum belegen darf.
  function placeSubtree(s, left) {
    const l = layoutOf(s);
    const slotX = left - l.L;
    placeSlot(s, slotX, genY(genOfSlot.get(s)));
    l.groups.forEach(g => {
      let x = slotX + g.x;
      g.kids.forEach(k => { placeSubtree(k, x); x += subW(k) + H_GAP; });
    });
  }

  // Wie viele Generationen reicht ein Teilbaum nach unten?
  function depthOf(s) {
    const kids = kidsOfSlot.get(s) || [];
    return 1 + (kids.length ? Math.max(...kids.map(depthOf)) : 0);
  }

  // Wurzeln: oberste Generation + Slots ohne Eltern-Slot (z.B. ohne erfasste Eltern)
  const roots = [];
  sortedGens.forEach(g => slotsByGen.get(g).forEach(s => { if (!parentSlotOf.has(s)) roots.push(s); }));

  // Mehrere Wurzeln (z.B. zwei Familien ohne gemeinsame Vorfahren) werden nebeneinander
  // gesetzt. cursor merkt sich pro Generation, wie weit rechts schon etwas steht.
  const cursor = new Map(); // gen -> rechter Rand
  roots.forEach(r => {
    const g0 = genOfSlot.get(r);
    const d  = depthOf(r);
    let left = null;
    for (let g = g0; g < g0 + d; g++) {
      if (cursor.has(g)) left = Math.max(left ?? -Infinity, cursor.get(g) + H_GAP);
    }
    if (left === null) left = 0;
    placeSubtree(r, left);
    const right = left + subW(r);
    for (let g = g0; g < g0 + d; g++) cursor.set(g, Math.max(cursor.get(g) ?? -Infinity, right));
  });

  // Ganzen Baum um x = 0 zentrieren
  const xs = [...positions.values()].map(p => p.x);
  if (xs.length) {
    const shift = -(Math.min(...xs) + Math.max(...xs) + CARD_W) / 2;
    positions.forEach(p => { p.x += shift; });
  }

  return positions;
}

// ─── 3. VERBINDUNGSLINIEN ────────────────────────────────────────────────────

// Liefert eine Liste von Linien, jede als SVG-Pfad („M x y L x y“ = von Punkt zu Punkt).
// type: 'partner' (durchgezogen), 'ex-partner' (gestrichelt) oder 'connector' (Eltern → Kinder).
export function computeLines(relationships, positions) {
  const lines = [];
  // Die Linien ragen 6 px in die Karten hinein, damit keine Lücke sichtbar ist.
  const OVERLAP = 6;

  // ── Partner-Linien (aktuelle Partner durchgezogen, Expartner gestrichelt) ──
  // Stehen zwei Partner direkt nebeneinander: gerade Linie auf halber Kartenhöhe.
  // Steht eine andere Karte dazwischen (z.B. Erika mit zwei Expartnern links), würde
  // eine gerade Linie durch diese Karte gehen – es sähe aus, als wären die Expartner
  // miteinander verbunden. Darum läuft die Linie dann als „Brücke“ ÜBER die Karten:
  // von der Person mit mehreren Partnern (hub) hinauf, waagerecht über die Karten
  // dazwischen, in der Lücke neben dem Expartner hinunter und seitlich in seine Karte.
  // Je weiter weg der Partner, desto höher die Brücke (sie kreuzen sich nie).
  // dropOf merkt sich pro Paar, wo die Linie zu den gemeinsamen Kindern beginnt:
  // immer in der Lücke direkt neben dem Partner, also auf der Linie des Paares.
  const BRIDGE_STEP = 10;
  const dropOf = new Map();        // "idA|idB" → x der Kinder-Linie
  const reserveAbove = new Map();  // Kartenreihe (y) → Platz, den Brücken oben brauchen
  const partnerCount = new Map();
  relationships.forEach(r => {
    if (r.type !== 'partner' && r.type !== 'ex-partner') return;
    partnerCount.set(r.from, (partnerCount.get(r.from) || 0) + 1);
    partnerCount.set(r.to, (partnerCount.get(r.to) || 0) + 1);
  });
  const allPos = [...positions.entries()];
  const drawnPairs = new Set();
  relationships.forEach(r => {
    if (r.type !== 'partner' && r.type !== 'ex-partner') return;
    const key = [r.from, r.to].sort().join('|');
    if (drawnPairs.has(key)) return;
    drawnPairs.add(key);
    const a = positions.get(r.from);
    const b = positions.get(r.to);
    if (!a || !b) return;
    const dash = r.type === 'ex-partner' ? '6,4' : undefined;
    const left  = a.x <= b.x ? a : b;
    const right = a.x <= b.x ? b : a;
    const y     = left.y + CARD_H / 2;
    // Karten derselben Reihe zwischen den beiden Partnern
    const between = Math.abs(a.y - b.y) < 1
      ? allPos.filter(([, p]) => Math.abs(p.y - a.y) < 1 && p.x > left.x + 1 && p.x < right.x - 1)
      : [];
    // hub = Person mit mehr Partnern, other = der andere (meist der Expartner)
    const fromIsHub = (partnerCount.get(r.from) || 0) >= (partnerCount.get(r.to) || 0);
    const hub   = fromIsHub ? a : b;
    const other = fromIsHub ? b : a;
    // Die Kinder-Linie beginnt in der Lücke direkt neben „other“ (bei einem normalen
    // Paar ist das genau die Mitte zwischen den beiden).
    const nearOther = other.x < hub.x ? other.x + CARD_W + COUPLE_GAP / 2 : other.x - COUPLE_GAP / 2;
    if (!between.length) {
      lines.push({ type: r.type, dash, path: `M ${left.x + CARD_W - OVERLAP} ${y} L ${right.x + OVERLAP} ${y}` });
      dropOf.set(key, nearOther);
      return;
    }
    const n = between.length;
    const bridgeY = hub.y - BRIDGE_STEP * n;
    const otherLeft = other.x < hub.x;
    const gapX = nearOther;
    const hubX = otherLeft ? hub.x + 2 + BRIDGE_STEP * n : hub.x + CARD_W - 2 - BRIDGE_STEP * n;
    const otherEdge = otherLeft ? other.x + CARD_W - OVERLAP : other.x + OVERLAP;
    lines.push({
      type: r.type, dash,
      path: `M ${hubX} ${hub.y + OVERLAP} L ${hubX} ${bridgeY} L ${gapX} ${bridgeY} L ${gapX} ${y} L ${otherEdge} ${y}`,
    });
    dropOf.set(key, gapX);
    reserveAbove.set(Math.round(hub.y), Math.max(reserveAbove.get(Math.round(hub.y)) || 0, BRIDGE_STEP * n + 8));
  });

  // Eltern-Kind-Linien
  // Anker-X = Mittelpunkt zwischen den zwei Eltern (nicht Durchschnitt aller)
  // Bei single parent: Mitte dieser Person
  // Kinder nach ihren Eltern gruppieren: Geschwister mit denselben Eltern hängen an einer gemeinsamen Linie.
  const groups = new Map();
  relationships.forEach(r => {
    if (r.type !== 'parent-child') return;
    if (!positions.has(r.from) || !positions.has(r.to)) return;
    const parentKey = relationships
      .filter(r2 => r2.type === 'parent-child' && r2.to === r.to && positions.has(r2.from))
      .map(r2 => r2.from).sort().join('|');
    if (!groups.has(parentKey)) groups.set(parentKey, new Set());
    groups.get(parentKey).add(r.to);
  });

  const bars = [];
  groups.forEach((childIds, parentKey) => {
    const pIds = parentKey.split('|').filter(Boolean);
    const pPos = pIds.map(id => positions.get(id)).filter(Boolean);
    if (!pPos.length) return;
    const cIds = [...childIds];
    const cPos = cIds.map(id => positions.get(id)).filter(Boolean);
    if (!cPos.length) return;

    // Anker-X: ein Elternteil → Mitte seiner Karte; zwei Elternteile → dort, wo die
    // Linie des Paares in der Lücke neben dem Partner liegt (siehe dropOf oben).
    let parentCX;
    if (pPos.length === 1) {
      parentCX = pPos[0].x + CARD_W / 2;
    } else {
      const key = pIds.slice(0, 2).sort().join('|');
      parentCX = dropOf.has(key)
        ? dropOf.get(key)
        : (Math.min(pPos[0].x, pPos[1].x) + CARD_W + Math.max(pPos[0].x, pPos[1].x)) / 2;
    }

    // Linie startet bei der vertikalen Mitte der Eltern (= Partner-Linie)
    const cXs = cPos.map(p => p.x + CARD_W / 2);
    bars.push({
      parentCX,
      parentMidY:    pPos[0].y + CARD_H / 2,
      parentBottomY: pPos[0].y + CARD_H,
      childTopY:     Math.min(...cPos.map(p => p.y)),
      cIds,
      hMinX: Math.min(parentCX, ...cXs),
      hMaxX: Math.max(parentCX, ...cXs),
    });
  });

  // ── Waagerechte Linien auf "Spuren" verteilen ─────────────────────────
  // Überlappen sich zwei waagerechte Linien zwischen denselben Generationen,
  // bekommen sie unterschiedliche Höhen, damit sie sich nie berühren.
  const LANE_GAP = 14; // Mindestabstand in x zwischen Linien auf derselben Spur
  const byLevel = new Map();
  bars.forEach(b => {
    const key = Math.round(b.parentBottomY);
    if (!byLevel.has(key)) byLevel.set(key, []);
    byLevel.get(key).push(b);
  });
  byLevel.forEach(levelBars => {
    levelBars.sort((a, b) => a.hMinX - b.hMinX);
    const laneEnds = [];
    levelBars.forEach(b => {
      let lane = laneEnds.findIndex(end => b.hMinX > end + LANE_GAP);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(b.hMaxX); }
      else laneEnds[lane] = b.hMaxX;
      b.lane = lane;
    });
    const nLanes = laneEnds.length;
    levelBars.forEach(b => {
      // Oben in der Kinder-Reihe Platz für Partner-Brücken freihalten
      const gap = b.childTopY - b.parentBottomY - (reserveAbove.get(Math.round(b.childTopY)) || 0);
      b.midY = b.parentBottomY + gap * (b.lane + 1) / (nLanes + 1);
    });
  });

  // Pro Elterngruppe drei Linienstücke: senkrecht nach unten, waagerecht über alle
  // Kinder, und senkrecht hinunter zu jedem Kind.
  bars.forEach(b => {
    // Senkrecht von Eltern-Mitte nach midY
    lines.push({ type: 'connector', path: `M ${b.parentCX} ${b.parentMidY} L ${b.parentCX} ${b.midY}` });
    // Waagerechte Linie bei midY
    if (b.hMaxX - b.hMinX > 1) {
      lines.push({ type: 'connector', path: `M ${b.hMinX} ${b.midY} L ${b.hMaxX} ${b.midY}` });
    }
    // Senkrecht von midY zu jedem Kind
    b.cIds.forEach(cid => {
      const cp = positions.get(cid);
      if (!cp) return;
      lines.push({ type: 'connector', path: `M ${cp.x + CARD_W / 2} ${b.midY} L ${cp.x + CARD_W / 2} ${cp.y}` });
    });
  });

  return lines;
}
